import React from 'react'
import saveDesignTemplate from '@/services/DesignTemplateService/saveDesignTemplate'
import { EMPTY_TEMPLATE_DATA, SaveDesignTemplateRequest } from '@/types/designTemplate'
import { normalizeTemplateData, validateTemplateData } from './normalizeTemplateData'

const saveDesignTemplateStates = {
  validation: {
    main: async (_data: any, defaultData: any) => {
      const validationAlert = await defaultData.formRef.current?.validate()
      if (validationAlert) throw new Error('Validation failed intentionally')
      return true
    },
    startMessage: () => 'Validate form ...',
    errorMessage: () => 'Some fields are required!',
  },

  form: {
    main: async (_data: any, defaultData: any) => defaultData.formRef.current?.getData(),
    startMessage: () => 'Read template details ...',
  },

  // The design payload must be section independent before it is stored: a
  // template is applied away from wherever it was authored.
  design: {
    main: async (data: any, defaultData: any) => {
      const source = defaultData.templateData ?? defaultData.currentTemplate?.data ?? EMPTY_TEMPLATE_DATA
      const normalized = normalizeTemplateData(source)

      // Remember the product chosen on the form so the designer knows what to
      // load, and so reopening the template does not ask again. The customizer
      // fills in the rest of origin when it saves a design.
      const picked = data.form.product
      const sku = typeof picked === 'object' ? picked?.value : picked
      if (sku) {
        const product = typeof picked === 'object' ? picked?.data : null
        // The section is chosen in the customizer, on the real product, so the
        // design keeps whichever one it was drawn on.
        normalized.origin = {
          ...(normalized.origin || {}),
          sku,
          ...(product?.product_id ? { product_id: product.product_id } : {}),
        }
      }

      const problem = validateTemplateData(normalized)
      if (problem) throw new Error(problem)

      return normalized
    },
    startMessage: () => 'Prepare design ...',
    errorMessage: (error: any) => <span>{error?.message || 'The design could not be prepared :('}</span>,
  },

  requestData: {
    main: async (data: any, defaultData: any): Promise<SaveDesignTemplateRequest> => {
      const isGeneric = data.form.isGeneric !== false

      return {
        name: data.form.name,
        description: data.form.description || '',
        // Written by the customizer when a design is saved: the same section
        // screenshot an order gets. Kept as-is from here.
        thumbnail_url: defaultData.currentTemplate?.thumbnail_url || '',
        is_active: data.form.isActive !== false,
        is_generic: isGeneric,
        priority: defaultData.currentTemplate?.priority ?? 0,
        data: data.design,
        // A generic template is offered on every section, so pinning it to
        // specific ones is contradictory and the API refuses it. Sections are
        // assigned from the product profile section form, not from here.
        section_ids: isGeneric ? [] : defaultData.currentTemplate?.sections?.map((s: any) => s.id) || [],
      }
    },
    startMessage: () => 'Prepare request ...',
    errorMessage: () => 'Prepare request failed :(',
  },

  saveTemplate: {
    main: async (data: any, defaultData: any) => {
      const result = await saveDesignTemplate(data.requestData, defaultData.currentTemplate?.id)
      // A create returns the new id; an update returns a message. Either way the
      // caller needs an id to open the designer on.
      const id = result?.id || result?.data?.id || defaultData.currentTemplate?.id
      return { ...result, id }
    },
    startMessage: () => 'Save template ...',
    errorMessage: (error: any) => {
      const apiError = error?.response?.data?.error
      if (!apiError) return <span>{error?.message || 'Save template failed :('}</span>

      if (apiError === 'design_template_name_exist') {
        return <span>A template with that name already exists.</span>
      }
      return <span className="capitalize">{String(apiError).replace(/_/g, ' ')}</span>
    },
  },

  reload: {
    main: async (data: any, defaultData: any) => {
      await defaultData.onSaved?.({
        id: data.saveTemplate?.id,
        sku: data.design?.origin?.sku,
        sectionId: data.design?.origin?.section_id,
      })
      return true
    },
    startMessage: () => 'Refresh templates ...',
    errorMessage: () => 'Refreshing failed :(',
  },
}

export default saveDesignTemplateStates
